import { serverName } from '../format'
import type { LiveModel } from '../adapter'
import { videoLabel } from '../setup'
import { t } from '../../i18n'
import { Icon } from '../../ui'
import { multitrack } from '../tracks'
import { trackCount } from './InputTracks'

/** The system's lock icon (TLS / SRT encryption next to the protocol). */
export function LockIcon({ size = 12 }: { size?: number }) {
  return <Icon name="lock" size={size <= 12 ? 12 : 16} />
}

const ms = (v?: number) => (v === undefined ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`)

/**
 * One line under the stage: how the stream comes in and its numbers, by protocol
 * (InputView.protocol): bitrate, video, keyframes, then for SRT the link (RTT,
 * latency, lost / resent: network.srt) and for RTMP(S) RTT and jitter. A press opens
 * the Input panel. Labels in the system's `label` voice (mono, uppercase), values as `metric`
 * (MetricsTable, AUDIT CB-10).
 */
export function InputStrip({ m, phone, open, onOpen }: { m: LiveModel; phone?: boolean; open?: boolean; onOpen: () => void }) {
  const src = m.source
  const s = m.summary
  const live = s.live && !src.unknown
  const proto = src.protocol?.toUpperCase()
  const base: [string, string][] = [
    [t('strip.bitrate'), live && s.bitrateMbps ? `${s.bitrateMbps.toFixed(1)} Mbps` : '—'],
    [t('strip.video'), live ? videoLabel(s.resolution, s.fps) ?? '—' : '—'],
    [t('strip.keyframes'), live && s.keyframeSec ? `${s.keyframeSec.toFixed(1)} s` : '—'],
  ]
  const link: [string, string][] = src.srt
    ? [[t('strip.rtt'), ms(src.srt.rttMs)], [t('strip.latency'), ms(src.srt.latencyMs)], [t('strip.lostResent'), `${src.srt.lost ?? '—'} / ${src.srt.resent ?? '—'}`]]
    : live ? [[t('strip.rtt'), src.delay && src.delay.rttMs > 0 ? `${Math.round(src.delay.rttMs)} ms` : '—'], [t('strip.jitter'), src.jitterMs !== undefined ? `${src.jitterMs} ms` : '—']] : []
  const cells = phone ? base : [...base, ...link]
  // E1 (design system InputTracks «Input row»): "3 V · 2 A" after the bitrate, behind a line-2 rule, only for a
  // multitrack stream and not on phones (there it heads the Input sheet)
  const tracks = !phone && live && multitrack(m.input?.tracks) ? trackCount(m.input?.tracks) : undefined
  const summary = [proto ?? t('strip.notConnected'), ...cells.map(([k, v]) => `${k} ${v}`), ...(tracks ? [`${t('tracks.strip')} ${tracks}`] : [])].join(', ')
  return (
    <button type="button" data-testid="input-strip" data-protocol={src.protocol ?? ''} aria-expanded={open} onClick={onOpen}
      aria-label={`${t('strip.edit')} — ${summary}`}
      className={`flex w-full flex-none flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border bg-panel px-4 py-2.5 text-left hover:border-line-2 ${open ? 'border-accent' : 'border-line'}`}>
      <span className="flex flex-col">
        <span className="sx-label">{t('strip.input')}</span>
        <span className="flex items-center gap-1.5 font-mono text-base leading-5 font-medium text-accent">
          {(src.protocol === 'rtmps' || src.protocol === 'srt') && <LockIcon />}
          <span data-testid="source-protocol">{proto ?? '—'}</span>
          <Icon name="chevron-down" size={16} />
        </span>
      </span>
      {cells.map(([k, v], i) => (
        <span key={k} className="contents">
          <span className="flex flex-col">
            <span className="sx-label">{k}</span>
            <span className="tabular font-mono text-base leading-5 font-medium">{v}</span>
          </span>
          {i === 0 && tracks && (
            <span data-testid="strip-tracks" className="flex flex-col border-l border-line-2 pl-6">
              <span className="sx-label">{t('tracks.strip')}</span>
              <span className="tabular font-mono text-base leading-5 font-medium">{tracks}</span>
            </span>
          )}
        </span>
      ))}
      {!phone && src.node && (
        <span data-testid="source-node" className={`ml-auto font-mono text-[11px] ${src.unknown ? 'text-warn' : 'text-muted'}`}>
          {serverName(src.node)}{src.unknown ? ` · ${t('strip.unreachable')}` : ''}
        </span>
      )}
    </button>
  )
}
